import { useMemo, useEffect, useState } from 'react';
import Autoplay from 'embla-carousel-autoplay';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from '@/shared/widgets/carousel';
import { resolveImageUrl } from '@/shared/utils/imageUtils';
import { TAILWIND_MD_MAX_PX, TAILWIND_MD_MIN_PX } from '@/shared/constants/breakpoints';
import { AnalyticsBannerLink } from '@/shared/features/analytics/presentation/components';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';

interface CarouselImage {
  width: number;
  url: string;
}

interface CarouselItem {
  position?: number;
  title: string;
  totalDesigns?: string;
  startingPrice?: string;
  link: string;
  imageUrl: string;
  images?: CarouselImage[];
  videoUrl?: string;
  isVideo?: boolean;
  autoplay?: string;
  playsinline?: string;
  controls?: string;
  loop?: string;
  muted?: string;
  tncLink?: string;
  shouldRefresh?: boolean;
}

interface CarouselSectionProps {
  data: Record<string, unknown> & {
    title?: string[];
    slideInterval?: number;
    showTitle?: boolean;
    items?: CarouselItem[];
  };
  sectionKey: string;
  eventLabel?: string;
  priority?: number;
}

export default function CarouselSection({
  data,
  sectionKey: _sectionKey,
  eventLabel: _eventLabel,
  priority: _priority,
}: CarouselSectionProps) {
  const items = (data.items || []) as CarouselItem[];
  const slideInterval = data.slideInterval || 3000;
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const { analytics, isInitialized } = useAnalytics();
  
  // Create autoplay plugin - recreates when slideInterval changes
  const autoplayPlugin = useMemo(
    () =>
      Autoplay({
        delay: slideInterval,
        stopOnInteraction: true,
        stopOnMouseEnter: true,
      }),
    [slideInterval]
  );

  // Update current slide index when carousel changes
  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap());

    const handleSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };

    api.on('select', handleSelect);
    return () => {
      api.off('select', handleSelect);
    };
  }, [api]);

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <section className="relative w-full">
      <Carousel
        setApi={setApi}
        onUserSelect={(selectedIndex) => {
          if (!analytics || !isInitialized) return;
          const selectedItem = items[selectedIndex];
          if (!selectedItem) return;

          try {
            analytics.trackEvent('Home Page Banner Viewed', {
              'Banner Name': selectedItem.title,
              'Banner Position': selectedIndex + 1,
              'Banner Type': 'banner',
              'Section Name': 'Banner Carousel',
            });
          } catch (error) {
            console.error('Error tracking Home Page Banner Viewed:', error);
          }
        }}
        plugins={items.length > 1 ? [autoplayPlugin] : undefined}
        opts={{
          align: 'start',
          loop: items.length > 1,
        }}
        className="w-full"
      >
        <CarouselContent className="-ml-0">
          {items.map((item, index) => (
            <CarouselItem key={`${item.title}-${index}`} className="pl-0">
              <AnalyticsBannerLink
                to={item.link}
                bannerName={item.title}
                bannerType="banner"
                fromNavBar={false}
                className="block w-full relative"
                title={item.title}
                aria-label={item.title}
                shouldRefresh={item.shouldRefresh}
              >
                {/* Reserve space up-front to avoid CLS while media loads */}
                <div className="relative w-full overflow-hidden bg-slate-100 aspect-[564/415] md:aspect-[595/193]">
                  {/* Poster placeholder (prevents "blank" before video/image paint) */}
                  {item.imageUrl ? (
                    <img
                      src={resolveImageUrl(item.imageUrl)}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="eager"
                      decoding={index === 0 ? 'sync' : 'async'}
                      fetchPriority={index === 0 ? 'high' : 'auto'}
                    />
                  ) : null}

                  {item.isVideo && item.videoUrl ? (
                    <video
                      className="absolute inset-0 w-full h-full object-cover"
                      title={item.title}
                      autoPlay={!!item.autoplay}
                      playsInline={!!item.playsinline}
                      controls={!!item.controls}
                      loop={!!item.loop}
                      muted={!!item.muted}
                      preload={index === 0 ? 'auto' : 'metadata'}
                      // @ts-expect-error - fetchPriority is valid for video elements but not in React types yet
                      fetchPriority={index === 0 ? 'high' : 'auto'}
                      poster={item.imageUrl ? resolveImageUrl(item.imageUrl) : undefined}
                    >
                      <source src={resolveImageUrl(item.videoUrl)} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  ) : item.images && item.images.length > 0 ? (
                    <picture className="absolute inset-0 block w-full h-full">
                      {/* Desktop images: 1200w and 2400w */}
                      <source
                        media={`(min-width: ${TAILWIND_MD_MIN_PX}px)`}
                        srcSet={item.images
                          .filter((img) => img.width === 1200 || img.width === 2400)
                          .map((img) => `${resolveImageUrl(img.url)} ${img.width}w`)
                          .join(', ')}
                        sizes="100vw"
                      />
                      {/* Mobile images: 750w and 1500w */}
                      <source
                        media={`(max-width: ${TAILWIND_MD_MAX_PX}px)`}
                        srcSet={item.images
                          .filter((img) => img.width === 750 || img.width === 1500)
                          .map((img) => `${resolveImageUrl(img.url)} ${img.width}w`)
                          .join(', ')}
                        sizes="100vw"
                      />
                      {/* Fallback image */}
                      <img
                        src={resolveImageUrl(item.imageUrl)}
                        alt={item.title}
                        className="w-full h-full object-cover"
                        loading={index === 0 ? 'eager' : 'lazy'}
                        decoding={index === 0 ? 'sync' : 'async'}
                        fetchPriority={index === 0 ? 'high' : 'auto'}
                      />
                    </picture>
                  ) : item.imageUrl ? (
                    <img
                      src={resolveImageUrl(item.imageUrl)}
                      alt={item.title}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading={index === 0 ? 'eager' : 'lazy'}
                      decoding={index === 0 ? 'sync' : 'async'}
                      fetchPriority={index === 0 ? 'high' : 'auto'}
                    />
                  ) : null}
                </div>
              </AnalyticsBannerLink>
            </CarouselItem>
          ))}
        </CarouselContent>

        {/* Navigation Arrows */}
        {items.length > 1 && (
          <>
            <CarouselPrevious className="left-2 md:left-4 bg-white/80 hover:bg-white/90 border-0 shadow-md" />
            <CarouselNext className="right-2 md:right-4 bg-white/80 hover:bg-white/90 border-0 shadow-md" />
          </>
        )}
      </Carousel>

      {/* Pagination Dots */}
      {items.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2">
          {items.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => api?.scrollTo(index)}
              className={`h-2 w-2 rounded-full transition-all duration-200 ${
                index === current
                  ? 'bg-white w-6'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
